import { addCollabBreadcrumb } from "@/shared/utils/sentry-helpers";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useYjsTransportStatus } from "../yjs/transport/status-context";

type Status = "connected" | "reconnecting" | "disconnected";
const StatusContext = createContext<Status>("connected");

export const useYjsConnectionStatus = () => useContext(StatusContext);

const RECONNECT_THRESHOLD_MS = 5000;

// Shared banner + context value for both transport paths.
const StatusBanner = ({
  status,
  children,
}: {
  status: Status;
  children: ReactNode;
}) => (
  <StatusContext.Provider value={status}>
    {status !== "connected" && (
      <div
        role="status"
        className="tw-fixed tw-top-0 tw-inset-x-0 tw-z-50 tw-bg-amber-500 tw-text-white tw-text-center tw-py-2"
      >
        {status === "disconnected"
          ? "Collaboration disconnected - recent changes may not be saved"
          : "Reconnecting collaboration..."}
      </div>
    )}
    {children}
  </StatusContext.Provider>
);

export const YjsConnectionStatusProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  return <HocuspocusConnectionStatus>{children}</HocuspocusConnectionStatus>;
};

// Drive the banner from the transport status published
// by YjsStoryProvider, applying the same reconnect debounce.
const HocuspocusConnectionStatus = ({ children }: { children: ReactNode }) => {
  const transportStatus = useYjsTransportStatus();
  const [status, setStatus] = useState<Status>("connected");
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    addCollabBreadcrumb(`yjs transport status: ${transportStatus}`);
    const clearTimer = () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    };
    if (transportStatus === "connected") {
      clearTimer();
      setStatus("connected");
      return;
    }
    if (transportStatus === "disconnected") {
      clearTimer();
      setStatus("disconnected");
      return;
    }
    // "reconnecting" — delay surfacing it, matching the Liveblocks debounce.
    if (reconnectTimerRef.current) return;
    reconnectTimerRef.current = setTimeout(() => {
      // Null the ref as the timer fires so a later reconnect cycle starts a fresh
      // debounce instead of bailing on a stale (already-fired) timer id.
      reconnectTimerRef.current = null;
      setStatus("reconnecting");
    }, RECONNECT_THRESHOLD_MS);
  }, [transportStatus]);

  useEffect(
    () => () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    },
    [],
  );

  return <StatusBanner status={status}>{children}</StatusBanner>;
};
